import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { QuoteDocument } from "../components/QuoteDocument";
import { api, formatApiError } from "../lib/api";

function DecisionBox({ token, onDone }) {
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const decide = async (accept) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/public/quotes/${token}/decision`, { name, accept });
      onDone(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="no-print mx-auto mt-6 max-w-3xl rounded-xl border border-teal-200 bg-white p-6" data-testid="quote-decision-box">
      <h2 className="text-lg font-semibold text-slate-900">Valider ce devis en ligne</h2>
      <p className="mt-1 text-sm text-slate-500">Indiquez votre nom complet : il fera office de « bon pour accord ».</p>
      <Input data-testid="decision-name-input" className="mt-4" placeholder="Prénom et nom" value={name} onChange={(e) => setName(e.target.value)} />
      <label className="mt-3 flex items-start gap-2 text-sm text-slate-600">
        <Checkbox data-testid="decision-agree-checkbox" checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" />
        J'accepte le devis et les conditions générales de vente.
      </label>
      {error && <p className="mt-3 text-sm text-red-600" data-testid="decision-error">{error}</p>}
      <div className="mt-5 flex flex-wrap gap-2">
        <Button data-testid="decision-accept-button" disabled={busy || !agree || name.trim().length < 2} onClick={() => decide(true)} className="rounded-full bg-[#0b2c6b] hover:bg-[#1559d6]">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}Accepter le devis
        </Button>
        <Button data-testid="decision-refuse-button" variant="outline" disabled={busy || name.trim().length < 2} onClick={() => decide(false)} className="rounded-full">
          <XCircle className="mr-2 h-4 w-4" />Refuser
        </Button>
      </div>
    </div>
  );
}

export default function PublicQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/quotes/${token}`).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  }, [token]);

  if (error) return <div className="flex min-h-screen items-center justify-center p-6 text-slate-600" data-testid="public-quote-error">{error}</div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1559d6]" /></div>;
  const { quote, company } = data;
  const decided = quote.status === "accepte" || quote.status === "refuse";

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="no-print mx-auto mb-4 flex max-w-3xl items-center justify-between">
        <p className="text-sm text-slate-500">Devis proposé par <b className="text-slate-800">{company.name}</b></p>
        <Button variant="ghost" size="sm" data-testid="public-print-button" onClick={() => window.print()}><Printer className="mr-2 h-4 w-4" />Imprimer</Button>
      </div>
      {decided && (
        <div data-testid="public-quote-status" className={`no-print mx-auto mb-4 max-w-3xl rounded-xl p-4 text-sm font-semibold ${quote.status === "accepte" ? "bg-teal-50 text-teal-800" : "bg-red-50 text-red-700"}`}>
          {quote.status === "accepte" ? "Merci ! Votre accord a bien été enregistré. L'entreprise va vous recontacter pour organiser le déménagement." : "Votre refus a bien été transmis à l'entreprise."}
        </div>
      )}
      <QuoteDocument q={quote} company={company} />
      {!decided && <DecisionBox token={token} onDone={setData} />}
      <p className="mt-8 text-center text-xs text-slate-400">Propulsé par Logidem — le logiciel intelligent des déménageurs</p>
    </div>
  );
}
